// @owners { team: patients-team }
import { SPACING } from '@alto/design-library-tokens';
import { ActionSheetV2, LgPadding } from '@alto/design-system';
import React, { useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { useOrderPricing } from '~shared/queries';
import { type OrderPricingQueryData } from '~shared/queries/useOrderPricing';
import { type Prescription } from '~shared/types';
import { useTrackPriceBreakdownViewed } from '../hooks/useTrackPriceBreakdownViewed';
import { PaymentBreakdown, type PricingBreakdownPrescription } from './PaymentBreakdown';

type Props = {
  readonly origin: string;
  readonly prescriptions: Prescription[] | PricingBreakdownPrescription[];
  readonly forSinglePrescription?: boolean;
  readonly shipmentID?: number | null;
};

export const PricingBreakdownActionSheet = ({
  forSinglePrescription = false,
  origin,
  prescriptions,
  shipmentID,
}: Props) => {
  const { orderPricing }: { orderPricing: OrderPricingQueryData | undefined } = useOrderPricing({
    shipmentID: shipmentID || undefined,
  });
  // key to include in analytics event to enable querying 'item viewed' events for given 'payment breakdown viewed' event
  const eventUuid = useMemo(uuid, [prescriptions]);

  useTrackPriceBreakdownViewed({
    lineItems: orderPricing?.line_items,
    numberOfRxs: prescriptions.length,
    origin,
    breakdownKey: eventUuid,
  });

  return (
    <ActionSheetV2 analyticsName="pricing breakdown">
      <LgPadding topPadding={SPACING.STATIC.NONE}>
        <PaymentBreakdown
          orderPricing={orderPricing}
          prescriptions={prescriptions}
          forSinglePrescription={forSinglePrescription}
          origin={origin}
          eventUuid={eventUuid}
        />
      </LgPadding>
    </ActionSheetV2>
  );
};
